/**
 * 1.6.0 the PRISM MCP connector's install instructions (plan A5), as lib/agent-install.ts is the agent's: per OS the
 * download (from /downloads/mcp/<version>/), `chmod +x` and `codesign --force --sign -` on macOS, then
 * `prism-mcp setup --url <PRISM> --token <pmcp_…>`; the client snippets (Claude Desktop's claude_desktop_config.json
 * `{"mcpServers":{"prism":{"command":"/path/prism-mcp"}}}`, Claude Code's `claude mcp add prism -- /path/prism-mcp`, a
 * generic stdio command) and the update command. Used by Administration → Integrations and My account. Stream T's
 * file; step 0 fixed the exports.
 */
export type McpOs = 'darwin-arm64' | 'darwin-x64' | 'linux-x64' | 'linux-arm64' | 'win-x64';

export const MCP_OSES: readonly McpOs[] = ['darwin-arm64', 'darwin-x64', 'linux-x64', 'linux-arm64', 'win-x64'];

export interface McpInstallStep { title: string; command: string }

export interface McpClientSnippet { client: 'claude-desktop' | 'claude-code' | 'stdio'; title: string; text: string }

/** One file of /downloads/mcp/<version>/manifest.json (the storage agent's manifest format, scripts/lib/sea.ts). */
export interface McpDownloadFile { file: string; os: string; arch: string; size: number; sha256: string }
export interface McpDownloads { version: string; base: string; files: McpDownloadFile[] }

const isWindows = (os: McpOs) => os.startsWith('win');

export const MCP_OS_LABELS: Record<McpOs, string> = {
  'darwin-arm64': 'macOS · Apple silicon',
  'darwin-x64': 'macOS · Intel',
  'linux-x64': 'Linux · x64',
  'linux-arm64': 'Linux · arm64',
  'win-x64': 'Windows · x64',
};

/** The OS of a manifest entry (`win`/`x64` → `win-x64`), or null for one this page does not know. */
export function mcpOsOf(f: { os: string; arch: string }): McpOs | null {
  const os = `${f.os === 'windows' || f.os === 'win32' ? 'win' : f.os}-${f.arch}`;
  return (MCP_OSES as readonly string[]).includes(os) ? (os as McpOs) : null;
}

/** The download's file name: `prism-mcp-darwin-arm64.gz`, `prism-mcp-win-x64.exe.gz`. */
export const mcpFileName = (os: McpOs) => `prism-mcp-${os}${isWindows(os) ? '.exe' : ''}.gz`;

/** The folder the program and its prism-mcp.conf live in. */
export const mcpFolder = (os: McpOs) => (isWindows(os) ? '$env:USERPROFILE\\prism-mcp' : '~/prism-mcp');

/**
 * The program's full path as an assistant needs it (clients do not expand `~`): a stand-in the person adjusts; the
 * last install step prints the real one.
 */
export function mcpProgramPath(os: McpOs, user = 'you'): string {
  if (isWindows(os)) return `C:\\Users\\${user}\\prism-mcp\\prism-mcp.exe`;
  return os.startsWith('darwin') ? `/Users/${user}/prism-mcp/prism-mcp` : `/home/${user}/prism-mcp/prism-mcp`;
}

/** The install steps for one OS. Without a token the setup step shows where the token goes. */
export function mcpInstallSteps(os: McpOs, opts: { origin: string; version: string; token?: string }): McpInstallStep[] {
  const origin = opts.origin.replace(/\/$/, '');
  const url = `${origin}/downloads/mcp/${opts.version}/${mcpFileName(os)}`;
  const token = opts.token ?? '<your pmcp_ token>';
  if (isWindows(os)) {
    return [
      { title: 'Download the connector (PowerShell)', command: [
        `New-Item -ItemType Directory -Force "${mcpFolder(os)}" | Out-Null; Set-Location "${mcpFolder(os)}"`,
        `Invoke-WebRequest ${url} -OutFile prism-mcp.exe.gz`,
        '$i = [IO.File]::OpenRead("$PWD\\prism-mcp.exe.gz"); $o = [IO.File]::Create("$PWD\\prism-mcp.exe")',
        '$g = New-Object IO.Compression.GZipStream($i, [IO.Compression.CompressionMode]::Decompress); $g.CopyTo($o); $g.Close(); $o.Close(); $i.Close()',
        'Remove-Item prism-mcp.exe.gz',
      ].join('\n') },
      { title: 'Connect it to PRISM', command: `.\\prism-mcp.exe setup --url ${origin} --token ${token}` },
      { title: 'Show the path to give your assistant', command: '(Resolve-Path .\\prism-mcp.exe).Path' },
    ];
  }
  const download = [`mkdir -p ${mcpFolder(os)} && cd ${mcpFolder(os)}`, `curl -fsSL ${url} | gunzip > prism-mcp`];
  download.push(os.startsWith('darwin') ? 'chmod +x prism-mcp && codesign --force --sign - prism-mcp' : 'chmod +x prism-mcp');
  return [
    { title: 'Download the connector', command: download.join('\n') },
    { title: 'Connect it to PRISM', command: `./prism-mcp setup --url ${origin} --token ${token}` },
    { title: 'Show the path to give your assistant', command: 'echo "$PWD/prism-mcp"' },
  ];
}

/** A path as one shell word for `claude mcp add` (quoted when it has spaces or shell characters). */
const shellWord = (p: string) => (/^[\w@%+=:,./\\-]+$/.test(p) ? p : `"${p.replace(/(["\\$`])/g, '\\$1')}"`);

/** The client configuration snippets for an installed program path. */
export function mcpClientSnippets(programPath: string): McpClientSnippet[] {
  const path = programPath.trim() || '/path/to/prism-mcp';
  return [
    { client: 'claude-desktop', title: 'Claude Desktop (claude_desktop_config.json)', text: JSON.stringify({ mcpServers: { prism: { command: path } } }, null, 2) },
    { client: 'claude-code', title: 'Claude Code', text: `claude mcp add prism -- ${shellWord(path)}` },
    { client: 'stdio', title: 'Other MCP clients (stdio)', text: `Transport: stdio\nCommand: ${path}\nArguments: none` },
  ];
}

/** Update an installed connector: it downloads PRISM's version, checks the signature and replaces itself. */
export function mcpUpdateCommand(os: McpOs): string {
  return isWindows(os) ? `& "${mcpFolder(os)}\\prism-mcp.exe" update` : `${mcpFolder(os)}/prism-mcp update`;
}

/** The OS this browser runs on, as a download target; a Mac is taken as Apple silicon (a browser cannot tell). */
export function detectMcpOs(userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent, platform = typeof navigator === 'undefined' ? '' : navigator.platform ?? ''): McpOs | null {
  const ua = `${userAgent} ${platform}`;
  if (/Windows|Win64|Win32/i.test(ua)) return 'win-x64';
  if (/Mac/i.test(ua) && !/iPhone|iPad/i.test(ua)) return 'darwin-arm64';
  if (/Linux|X11/i.test(ua) && !/Android/i.test(ua)) return /aarch64|arm64/i.test(ua) ? 'linux-arm64' : 'linux-x64';
  return null;
}

/** Where Claude Desktop keeps its configuration file, per OS. */
export function claudeDesktopConfigPath(os: McpOs): string {
  if (isWindows(os)) return '%APPDATA%\\Claude\\claude_desktop_config.json';
  return os.startsWith('darwin') ? '~/Library/Application Support/Claude/claude_desktop_config.json' : '~/.config/Claude/claude_desktop_config.json';
}

/** Human-readable size. */
export const mcpSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
